import { lazy, useEffect, useRef, useState } from 'react'
import { CheckBox, NVButton } from '@nv/basic-components'
import { AuthCodeInput, useNVModal } from '@nv/components'
import { useRegistrationUserContext } from '@nv/contexts'
import VoterRegistrationService from 'libs/services/src/lib/voter/VoterRegistrationService'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import styles from './RegistrationStep3.module.scss'
const RegisterSuccess = lazy(() => import('@nv/lazy-loadable/lib/modals/RegisterSuccess'))

export const RegistrationStep3 = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [selectedOption, setSelectedOption] = useState(null)
  const [isNextClicked, setIsNextClicked] = useState(false)
  const [code, setCode] = useState('')
  const [codeError, setCodeError] = useState(false)
  const authInputRef = useRef(null)
  const [notValid, setNotValid] = useState(true)
  const [recapchaCode, setRecapchaCode] = useState(null)
  const [numOfAttempts, setNumOfAttempts] = useState(0)
  const [autoApproved, setAutoApproved] = useState(false)
  const { registrationUser: registrationData, setRegistrationUser } = useRegistrationUserContext()
  const { NVModal, openModal } = useNVModal()

  useEffect(() => {
    if (codeError) {
      const counter = numOfAttempts + 1
      setNumOfAttempts(counter)
      onHandleNumOfAttempts(counter)
    }
  }, [codeError])

  useEffect(() => {
    if (code.length) {
      setCodeError(false)
    }
    if (code.length === 6) {
      setNotValid(false)
      handleConfirmClick()
    } else {
      setNotValid(true)
    }
  }, [code])

  const onHandleNumOfAttempts = (counter) => {
    if (codeError && counter >= 2) {
      setCode('')
      authInputRef.current.focus()
    }
  }

  const handleOptionChange = async (option) => setSelectedOption(option === selectedOption ? null : option)

  const handleNextClick = async () => {
    if (selectedOption) {
      setIsNextClicked(true)
      if (selectedOption === 'SMS') VoterRegistrationService.sendSMSCode(registrationData.phone, recapchaCode)
      else VoterRegistrationService.sendEmailCode(registrationData.email, recapchaCode)
    } else {
      console.log('Please select an option.')
    }
  }

  const handleConfirmClick = async () => {
    try {
      if (registrationData) {
        const formData = registrationData.formData
        formData.append('verificationCode', code)

        let resp
        if (selectedOption === 'SMS') resp = await VoterRegistrationService.verifySMSCode(formData)
        else resp = await VoterRegistrationService.verifyEmailCode(formData)

        if (resp.success) {
          if (resp.status === 'Approved') setAutoApproved(true)
          openModal()
          setRegistrationUser(null)
          sessionStorage.clear()
          setTimeout(() => navigate('/'), 5000)
        } else if (resp.error) setCodeError(true)
      }
    } catch (error) {
      console.error('Error submitting registration data:', error)
    }
  }

  const checkBoxes = (
    <>
      <div className={styles['title']}>{t('registration.oneMoreStep')}</div>
      <div className={styles['description']}>{t('registration.weWillSendCode')}</div>
      <div className={styles['checkboxes']}>
        <CheckBox
          field='emailSelect'
          checked={selectedOption === 'Email'}
          onChange={() => handleOptionChange('Email')}
          label={t('registration.sendToEmail')}
          ariaLabel={t('registration.sendToEmail')}
        />
        <CheckBox
          field='smsSelect'
          checked={selectedOption === 'SMS'}
          onChange={() => handleOptionChange('SMS')}
          label={t('registration.sendToPhone')}
          ariaLabel={t('registration.sendToPhone')}
        />
      </div>
      <NVButton
        classes={{ container: styles['button'] }}
        text={t('buttons.next')}
        height={50}
        onChange={handleOptionChange}
        onClick={handleNextClick}
        disabled={!selectedOption}
      />
    </>
  )

  const verifyCode = (
    <>
      <div className={styles['title']}>{t('registration.enterCode')}</div>
      <div className={styles['description']}>
        {t(`registration.sentCode${selectedOption}`)} {t('registration.pleaseEnterCode')}
      </div>

      <AuthCodeInput authInputRef={authInputRef} code={code} setCode={setCode} length={6} codeError={codeError} />

      <div className={styles['details']}>
        <div className={styles['details__text']}>
          {t('registration.didntRecieveCode')}
          <NVButton
            classes={{ container: styles['details__btn'] }}
            variant='text'
            text={t('registration.resend')}
            onClick={() => {
              setCode('')
              setIsNextClicked(false)
            }}
          />
        </div>
        <div className={styles['details__text']}>
          {t('registration.wrongConctactInfo')}
          <NVButton classes={{ container: styles['details__btn'] }} variant='text' text={t('main.goBack')} onClick={() => navigate(-1)} />
        </div>
      </div>
      <NVButton
        classes={{ container: styles['button'] }}
        text={t('buttons.confirm')}
        height={50}
        width={'100%'}
        onClick={handleConfirmClick}
        disabled={notValid}
      />
    </>
  )

  return (
    <>
      <NVModal>
        <RegisterSuccess autoApproved={autoApproved} onClick={() => navigate('/')} />
      </NVModal>

      <div className={styles['container']}>{isNextClicked ? verifyCode : checkBoxes}</div>
    </>
  )
}
